/**
 * Empreendimentos de uma incorporadora.
 *
 * O botão de criar só aparece para quem pode escrever. Isso NÃO substitui a RLS — ela
 * continua sendo quem barra — mas evita o pior tipo de tela: a que oferece um botão e
 * responde "sem permissão" depois que o usuário preencheu tudo.
 */
import { h } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, tentar, vazio } from '../casca';
import type { Vista } from '../casca';
import { avisar, confirmar, perguntar } from '../ui/aviso';
import { botaoMenu } from '../ui/menu';
import { ir, recarregar } from '../rotas';
import {
  criarCenario, criarEmpreendimento, listarEmpreendimentos, listarEmpresas, pedirExclusao, renomearEmpreendimento,
} from '../../../../packages/dados/src/repositorios';
import type { Empresa, EmpreendimentoResumo } from '../../../../packages/dados/src/repositorios';
import { acessoDePlataforma, podeEditar } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';
import { avisoDeFora, textoSemEmpreendimentos } from '../acesso-plataforma';

function avisoPlataforma(ativa: boolean): HTMLElement {
  return h(
    'div',
    { class: 'aviso', style: 'margin-bottom:14px;display:flex;gap:9px;align-items:flex-start', 'data-el': 'EL-180' },
    h('span', { style: 'flex:none', html: icone.olho(15) }),
    /* D23 (12/10/2026, aprovado como proposto): desde a 0031 o Admin Fincast escreve em toda
       incorporadora ATIVA — o "pode consultar, mas não editar" ficou falso. Na inativa, ADM-3: o
       texto não promete editar (ver `acesso-plataforma.ts`). */
    h('span', {}, avisoDeFora(ativa)),
  );
}

function renomear(e: EmpreendimentoResumo): void {
  void (async () => {
    const nome = await perguntar({
      titulo: 'Renomear empreendimento', rotulo: 'Nome', valor: e.nome, confirmar: 'Salvar',
    });
    if (nome === null || nome === e.nome) return;
    await tentar(() => renomearEmpreendimento(e.id, nome), () => {
      avisar('Nome atualizado.');
      recarregar();
    });
  })();
}

function solicitarExclusao(e: EmpreendimentoResumo): void {
  void (async () => {
    const ok = await confirmar({
      titulo: 'Pedir a exclusão deste empreendimento?',
      corpo: [
        'Você vai enviar um pedido para excluir ',
        h('b', {}, e.nome),
        ' e os cenários dele. Quem decide é o administrador da plataforma — nada some agora.',
      ],
      confirmar: 'Enviar pedido',
      perigo: true,
    });
    if (!ok) return;
    await tentar(
      () => pedirExclusao({ alvo: 'empreendimento', empresaId: e.empresaId, empreendimentoId: e.id }),
      () => avisar('Pedido enviado. O administrador da plataforma vai avaliar.'),
    );
  })();
}

/**
 * Logo depois de "Novo empreendimento", o nome do 1º cenário — sem passar por tela vazia.
 * Combinado na v32 (item 5): "Escrevendo e dando o ok, abre-se essa nova operação para
 * coleta de inputs". Desistir leva ao estado vazio do empreendimento, que oferece o mesmo
 * botão; o empreendimento já existe e não some por isso.
 */
async function primeiroCenario(empresaId: string, empreendimentoId: string): Promise<void> {
  const vazio = (): void => ir({ nome: 'empreendimento', empresaId, empreendimentoId });
  const nome = await perguntar({
    titulo: 'Primeiro cenário', rotulo: 'Nome do cenário', dica: 'Ex.: Base, Otimista, Banco', confirmar: 'Criar',
  });
  if (nome === null) {
    vazio();
    return;
  }
  try {
    const novo = await criarCenario({ empreendimentoId, nome });
    ir({ nome: 'cenario', cenarioId: novo.id });
  } catch (e) {
    avisar(e instanceof Error ? e.message : String(e), 'erro');
    vazio();
  }
}

/** "1 cenário", "3 cenários" — os ATIVOS (o arquivado não conta, como no limite de 5). */
export function rotuloCenarios(n: number): string {
  return `${n} ${n === 1 ? 'cenário' : 'cenários'}`;
}

function linhaEmpreendimento(e: EmpreendimentoResumo, editavel: boolean): HTMLElement {
  /* Cai direto no cenário (o último aberto aqui, senão o mais antigo) — desde 09/10/2026
     não há mais a tela intermediária com a lista; ver `telas/empreendimento.ts`. */
  const abrir = () => ir({ nome: 'empreendimento', empresaId: e.empresaId, empreendimentoId: e.id });

  /* Renomear era um lápis solto no cartão. Virou item de menu porque agora são duas
     ações, e a segunda é destrutiva — botão vermelho ao lado do nome, num cartão que
     inteiro é clicável, é convite a erro. */
  const menu = botaoMenu(() => [
    { rotulo: 'Renomear', aoClicar: () => renomear(e) },
    { rotulo: 'Solicitar exclusão', perigo: true, aoClicar: () => solicitarExclusao(e) },
  ]);

  return h(
    'div',
    {
      class: 'card acard',
      style: 'padding:16px 18px;display:flex;align-items:center;gap:16px;cursor:pointer',
      onclick: abrir,
    },
    h('span', { style: 'color:hsl(var(--primary-text));flex:none', html: icone.predio(20) }),
    /* Decisão de 12/10/2026 (cadastro por cenário, 0032): o cartão mostra só o nome e quantos
       cenários ATIVOS. Programa, VGV e unidades saíram — são do cadastro, que agora é de cada
       cenário, e o cartão não escolhe um deles. "criado em" saiu junto (leitura literal). */
    h(
      'div',
      { style: 'display:grid;gap:3px;min-width:0;flex:1' },
      h('span', { style: 'font-weight:640' }, e.nome),
      h('span', { class: 'muted tnum', style: 'font-size:var(--fs-2xs)', 'data-cenarios': String(e.cenarios) },
        rotuloCenarios(e.cenarios)),
    ),
    editavel ? menu : null,
    h('span', { class: 'muted', style: 'flex:none', html: icone.seta(16) }),
  );
}

export async function telaEmpreendimentos(perfil: Perfil, empresaId: string): Promise<Vista> {
  /* As duas chamadas em paralelo: a lista de empresas é a fonte do NOME da incorporadora
     para a trilha — pedi-la depois deixaria o topo escrito "—" por um instante. */
  const [empresas, lista] = await Promise.all([listarEmpresas(), listarEmpreendimentos(empresaId)]);
  const empresa: Empresa | undefined = empresas.find((e) => e.id === empresaId);
  /* Fallback silencioso aqui já enganou na validação: a incorporadora tinha sido apagada
     e a tela mostrou "Incorporadora" com lista vazia, como se ela existisse e não tivesse
     nada dentro. Endereço que não resolve tem de DIZER que não resolveu. */
  if (empresa === undefined) {
    throw new Error('Esta incorporadora não existe mais, ou saiu do seu acesso.');
  }
  const nomeEmpresa = empresa.nome;
  /* ADM-2: a situação lida AGORA (esta lista) vale sobre o perfil em memória — ver `sessao.ts`. */
  const editavel = podeEditar(perfil, empresaId, empresa.ativa);

  /* Fábrica, não instância: o mesmo botão aparece no cabeçalho e no estado vazio, e
     `cloneNode` copiaria a marcação sem o ouvinte — um botão que não faz nada. */
  const botaoCriar = (): HTMLElement | null =>
    editavel
      ? h(
          'button',
          {
            class: 'btn pri',
            'data-el': 'EL-175',
            onclick: () => {
              void (async () => {
                const nome = await perguntar({
                  titulo: 'Novo empreendimento',
                  rotulo: 'Nome do empreendimento',
                  dica: 'Ex.: Residencial Aurora',
                });
                if (nome === null) return;
                await tentar(
                  () => criarEmpreendimento(empresaId, { nome }),
                  (novo) => void primeiroCenario(empresaId, novo.id),
                );
              })();
            },
          },
          h('span', { html: icone.mais(15) }),
          'Novo empreendimento',
        )
      : null;

  const corpo = h('div', { 'data-el': 'EL-174' });
  if (acessoDePlataforma(perfil, empresaId)) corpo.appendChild(avisoPlataforma(empresa.ativa));
  corpo.appendChild(
    cabecalhoDeTela(
      nomeEmpresa,
      /* Texto aprovado de antes, só sem "o cadastro" (falso desde a 0032: o cadastro é de cada
         cenário). Frase nova aqui só com aprovação — revisão de 12/10/2026. */
      'Cada empreendimento guarda os cenários de viabilidade.',
      h('button', {
        class: 'btn out',
        onclick: () => ir({ nome: 'equipe', empresaId }),
      }, h('span', { html: icone.olho(15) }), 'Equipe'),
      botaoCriar(),
    ),
  );

  corpo.appendChild(
    lista.length === 0
      ? vazio(
          /* ADM-3: na inativa a lista vem vazia porque a leitura filtra `ativa` — não "nenhum cadastrado" */
          textoSemEmpreendimentos({ editavel, ativa: empresa.ativa }),
          botaoCriar(),
        )
      : h('div', { style: 'display:grid;gap:10px;max-width:900px' },
          ...lista.map((e) => linhaEmpreendimento(e, editavel))),
  );

  return {
    trilha: [{ texto: 'Incorporadoras', rota: { nome: 'incorporadoras' } }, { texto: nomeEmpresa }],
    corpo,
    ativo: 'empreendimentos',
    empresaId,
  };
}
